import { flattenObj, expandObj } from '@utils/flatten-expand-object/flatten-expand-object';

/**
 * @description Function that cleans an object by removing all the keys with falsy values like:
 * `undefined`, `null`, `NaN`, `''`, `[]`, `{}` (empty object), `0`, `false`.
 * It parses the object recursively, so it will also remove the keys with falsy values from nested objects.
 *
 * @example
 * const obj = {
 *  a: 1,
 *  b: 0,
 *  c: undefined,
 *  d: null,
 *  e: '',
 *  f: [],
 *  g: {},
 *  h: false,
 *  i: 'Hello',
 *  j: [1, 2, 3],
 *  k: { a: 1, b: 2 },
 *  l: { c: 1, d: false },
 *  };
 *
 *  cleanObject(obj);
 *  // Output: { a: 1, i: 'Hello', j: [1, 2, 3], k: { a: 1, b: 2 }, l: { c: 1 } }
 */
export const cleanObject = <T extends Record<string, unknown>>(obj: T): T => {
  const flattenedObj = flattenObj(obj);

  Object.keys(flattenedObj).forEach((key) => {
    if (!flattenedObj[key]) {
      delete flattenedObj[key];
    } else if (Array.isArray(flattenedObj[key]) && !flattenedObj[key].length) {
      delete flattenedObj[key];
    } else if (typeof flattenedObj[key] === 'object' && !Object.keys(flattenedObj[key]).length) {
      delete flattenedObj[key];
    }
  });

  return expandObj(flattenedObj);
};
